import { useCallback, useEffect, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, CheckCircle2, AlertTriangle,
  Copy, ExternalLink, Radio, Map as MapIcon, Search, Clock, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { adminApi } from "../lib/adminAuth";

/** "12 minutes ago" — local helper, identical to AdminDashboard's. */
function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 45) return "just now";
  if (diffSec < 90) return "1 minute ago";
  if (diffSec < 60 * 45) return `${Math.round(diffSec / 60)} minutes ago`;
  if (diffSec < 60 * 90) return "1 hour ago";
  if (diffSec < 60 * 60 * 22) return `${Math.round(diffSec / 3600)} hours ago`;
  if (diffSec < 60 * 60 * 36) return "1 day ago";
  return `${Math.round(diffSec / 86400)} days ago`;
}

function StatusPill({ ok, okLabel, warnLabel, testid }) {
  return (
    <span
      data-testid={testid}
      className={
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider " +
        (ok
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
          : "bg-amber-50 text-amber-800 ring-1 ring-amber-200")
      }
    >
      {ok
        ? <CheckCircle2 className="h-3.5 w-3.5" />
        : <AlertTriangle className="h-3.5 w-3.5" />}
      {ok ? okLabel : warnLabel}
    </span>
  );
}

function HealthCell({ label, ok, value, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className={
        "rounded-xl border px-3.5 py-3 " +
        (ok
          ? "border-emerald-200 bg-emerald-50/60"
          : "border-amber-200 bg-amber-50/60")
      }
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
        {label}
      </p>
      <p className="mt-1 flex items-center gap-1.5 text-[13.5px] font-semibold text-stone-900">
        {ok
          ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
          : <AlertTriangle className="h-3.5 w-3.5 text-amber-700" />}
        <span>{value}</span>
      </p>
      {sub && (
        <p className="mt-0.5 text-[11px] text-stone-500 truncate">{sub}</p>
      )}
    </div>
  );
}

function CopyField({ value, testid }) {
  const onCopy = useCallback(() => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Copy failed"),
    );
  }, [value]);
  return (
    <div className="flex items-stretch gap-0 overflow-hidden rounded-lg border border-stone-200 bg-stone-50">
      <input
        readOnly
        value={value || ""}
        data-testid={testid}
        className="min-w-0 flex-1 bg-transparent px-3 py-2 font-mono text-xs text-stone-700 outline-none"
        onFocus={(e) => e.target.select()}
      />
      <button
        type="button"
        onClick={onCopy}
        data-testid={`${testid}-copy`}
        className="flex shrink-0 items-center gap-1.5 border-l border-stone-200 bg-white px-3 text-xs font-medium text-stone-700 hover:bg-stone-100"
      >
        <Copy className="h-3.5 w-3.5" /> Copy
      </button>
    </div>
  );
}

export default function AdminSeoSetup() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState(false);
  const [error, setError] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const [{ data }, { data: cron }, { data: health }] = await Promise.all([
        adminApi().get("/admin/v2/seo/gsc-status"),
        adminApi().get("/admin/v2/seo/indexnow-cron/status").catch(() => ({ data: null })),
        adminApi().get("/admin/v2/seo/health").catch(() => ({ data: null })),
      ]);
      setStatus({ ...data, cron, health });
      setError("");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error(`Failed to load SEO status: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const [cronBusy, setCronBusy] = useState(false);
  async function onCronRunNow() {
    if (cronBusy) return;
    setCronBusy(true);
    try {
      const { data } = await adminApi().post("/admin/v2/seo/indexnow-cron/run-now");
      const msg = data.status === "ok"
        ? `Pinged ${data.url_count} URL${data.url_count === 1 ? "" : "s"} ✓`
        : data.status === "noop"
          ? "No new pair URLs since last run"
          : data.status === "seeded"
            ? "Seeded high-water mark (no ping sent)"
            : `${data.url_count} URL${data.url_count === 1 ? "" : "s"} pinged with partial success`;
      (data.status === "ok" ? toast.success : toast.message)("Cron tick complete", { description: msg });
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Cron run failed");
    } finally {
      setCronBusy(false);
    }
  }

  async function onPing() {
    if (pinging) return;
    setPinging(true);
    try {
      const { data } = await adminApi().post("/admin/v2/seo/ping-search-engines");
      const ix = data?.indexnow || {};
      const okOverall = !!ix.ok;
      (okOverall ? toast.success : toast.message)(
        "Search engines pinged",
        {
          description: ix.skipped
            ? `IndexNow: skipped (${ix.skipped})`
            : okOverall
              ? `IndexNow: ${ix.status || "ok"} ✓`
              : `IndexNow: ${ix.status ?? "error"}`,
        },
      );
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Ping failed");
    } finally {
      setPinging(false);
    }
  }

  const gscOk = !!status?.gsc_verification_set;
  const indexnowOk = !!status?.indexnow_key_set;

  return (
    <main
      data-testid="admin-seo-setup-page"
      className="min-h-screen bg-[#fdf8f3] px-6 py-10"
    >
      <div className="mx-auto max-w-4xl">
        <AdminHeader />

        <h1 className="text-3xl font-semibold tracking-tight text-stone-900">
          Search Engine Setup
        </h1>
        <p className="mt-1 text-sm text-stone-600">
          One place to verify your Google Search Console property and push
          your sitemap to every engine that matters.
        </p>

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading status…
          </div>
        )}

        {error && !loading && (
          <p
            data-testid="seo-setup-error"
            className="mt-6 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </p>
        )}

        {status && !loading && (
          <>
            {/* Section 0 — SEO Health snapshot (always on top) */}
            {status.health?.indicators && (
              <section
                data-testid="seo-card-health"
                className="mt-8 rounded-2xl border border-stone-200 bg-white p-6 shadow-sm"
              >
                <header className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-base font-semibold text-stone-900">
                      SEO Health
                    </h2>
                    <p className="text-xs text-stone-500">
                      Live snapshot — refreshed on every page load.
                    </p>
                  </div>
                </header>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <HealthCell
                    label="GSC Status"
                    ok={status.health.indicators.gsc.ok}
                    value={status.health.indicators.gsc.label}
                    sub={(() => {
                      // Sub-label communicates *how* the property is verified.
                      // DNS-verified setups (the common case for properties
                      // that Google auto-verified via the apex TXT record)
                      // now show "DNS TXT" instead of falsely reading "Missing".
                      const m = status.health.indicators.gsc.mode;
                      if (m === "dns") return "DNS TXT verified";
                      if (m === "html-file") return "HTML file";
                      if (m === "meta-tag") return "Meta tag";
                      return "none";
                    })()}
                    testid="health-cell-gsc"
                  />
                  <HealthCell
                    label="Verification Route"
                    ok={status.health.indicators.verification_route.ok}
                    value={status.health.indicators.verification_route.label}
                    sub={
                      status.health.indicators.verification_route.applicable
                        ? "File-mode"
                        : "n/a"
                    }
                    testid="health-cell-verification"
                  />
                  <HealthCell
                    label="IndexNow"
                    ok={status.health.indicators.indexnow.ok}
                    value={status.health.indicators.indexnow.label}
                    testid="health-cell-indexnow"
                  />
                  <HealthCell
                    label="Sitemap"
                    ok={status.health.indicators.sitemap.ok}
                    value={status.health.indicators.sitemap.label}
                    sub={
                      status.health.indicators.sitemap.url_count
                        ? `${Number(status.health.indicators.sitemap.url_count).toLocaleString()} URLs`
                        : undefined
                    }
                    testid="health-cell-sitemap"
                  />
                </div>
              </section>
            )}

            {/* Section 1 — Google Search Console */}
            <section
              data-testid="seo-card-gsc"
              className="mt-8 rounded-2xl border border-stone-200 bg-white p-6 shadow-sm"
            >
              <header className="mb-4 flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-stone-100 p-2 text-stone-700">
                    <Search className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-stone-900">
                      Google Search Console
                    </h2>
                    <p className="text-xs text-stone-500">
                      Verify your domain so Google trusts the sitemap submission.
                    </p>
                  </div>
                </div>
                <StatusPill
                  ok={gscOk}
                  okLabel="Verified"
                  warnLabel="Not verified"
                  testid="gsc-status-pill"
                />
              </header>

              {gscOk ? (
                <div
                  data-testid="gsc-verified-block"
                  className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
                >
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="font-semibold">Property verified</p>
                      {status.gsc_verification_mode && (
                        <p className="mt-1 text-xs text-emerald-800">
                          Method:{" "}
                          <code className="rounded bg-white/70 px-1.5 py-0.5 font-mono text-[11px] text-emerald-900">
                            {status.gsc_verification_mode === "dns" && "DNS TXT record"}
                            {status.gsc_verification_mode === "html-file" && "HTML file"}
                            {status.gsc_verification_mode === "meta-tag" && "Meta tag"}
                          </code>
                          {status.gsc_verification_mode === "dns" && (
                            <span className="ml-2 text-emerald-700">
                              (auto-detected from your apex domain's TXT record — nothing more to configure)
                            </span>
                          )}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-emerald-800">
                        Domain:{" "}
                        <code className="rounded bg-white/70 px-1.5 py-0.5 font-mono text-[11px] text-emerald-900">
                          {status.domain}
                        </code>
                      </p>
                      <p className="mt-2 text-xs">
                        SSR pages auto-emit{" "}
                        <code className="font-mono text-[11px]">
                          &lt;meta name="google-site-verification"&gt;
                        </code>{" "}
                        on every render — no further action needed.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div data-testid="gsc-setup-guide" className="space-y-3">
                  <p className="text-sm text-stone-700">
                    Follow these 4 steps to unlock GSC indexing in under
                    five minutes:
                  </p>
                  <ol className="space-y-2 text-sm text-stone-700">
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-stone-900 text-xs font-semibold text-white">1</span>
                      <span>
                        Open{" "}
                        <a
                          href="https://search.google.com/search-console"
                          target="_blank"
                          rel="noreferrer"
                          className="font-semibold text-stone-900 underline underline-offset-2"
                        >
                          search.google.com/search-console
                        </a>{" "}
                        in a new tab.
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-stone-900 text-xs font-semibold text-white">2</span>
                      <span>
                        Click <em>Add property</em> → paste{" "}
                        <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[11px]">
                          {status.domain}
                        </code>
                        .
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-stone-900 text-xs font-semibold text-white">3</span>
                      <span>
                        Choose <em>HTML tag</em> verification → copy the{" "}
                        <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[11px]">
                          content="…"
                        </code>{" "}
                        value Google shows you.
                      </span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-stone-900 text-xs font-semibold text-white">4</span>
                      <span>
                        Set{" "}
                        <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[11px]">
                          GOOGLE_SITE_VERIFICATION=your-value
                        </code>{" "}
                        in{" "}
                        <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[11px]">
                          backend/.env
                        </code>{" "}
                        and restart the backend service.
                      </span>
                    </li>
                  </ol>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a
                      href={status.gsc_property_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="gsc-add-property-link"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-3.5 py-2 text-xs font-semibold text-white hover:bg-stone-800"
                    >
                      Open GSC <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadStatus}
                      data-testid="gsc-recheck-button"
                    >
                      I've added it — recheck
                    </Button>
                  </div>
                </div>
              )}
            </section>

            {/* Section 2 — Sitemap submission */}
            <section
              data-testid="seo-card-sitemap"
              className="mt-6 rounded-2xl border border-stone-200 bg-white p-6 shadow-sm"
            >
              <header className="mb-4 flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-stone-100 p-2 text-stone-700">
                    <MapIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-stone-900">
                      Sitemap submission
                    </h2>
                    <p className="text-xs text-stone-500">
                      {Number(status.sitemap_total_urls).toLocaleString()} URLs across{" "}
                      {status.sitemap_breakdown?.ingredients ?? 0} ingredients,{" "}
                      {status.sitemap_breakdown?.ingredient_pairs ?? 0} pairs,{" "}
                      {status.sitemap_breakdown?.products ?? 0} products,{" "}
                      {status.sitemap_breakdown?.medications ?? 0} medications.
                    </p>
                  </div>
                </div>
                <StatusPill
                  ok={indexnowOk}
                  okLabel="IndexNow active"
                  warnLabel="IndexNow off"
                  testid="indexnow-status-pill"
                />
              </header>

              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                    Sitemap URL
                  </label>
                  <CopyField value={status.sitemap_url} testid="sitemap-url" />
                </div>

                <div className="flex flex-wrap gap-2">
                  <a
                    href={status.gsc_sitemap_submission_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="gsc-submit-sitemap-button"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-3.5 py-2 text-xs font-semibold text-white hover:bg-stone-800"
                  >
                    Submit to Google Search Console{" "}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={onPing}
                    disabled={pinging}
                    data-testid="indexnow-ping-button"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-stone-900 hover:border-stone-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {pinging
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Radio className="h-3.5 w-3.5" />}
                    Ping IndexNow (Bing + Yandex)
                  </button>
                </div>

                <div
                  data-testid="last-ping-info"
                  className="rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-600"
                >
                  {status.last_ping?.pinged_at
                    ? (
                      <>
                        Last pinged{" "}
                        <strong className="text-stone-900">
                          {relativeTime(status.last_ping.pinged_at)}
                        </strong>
                        {status.last_ping.indexnow_status
                          ? ` · IndexNow returned ${status.last_ping.indexnow_status}`
                          : ""}
                        {status.last_ping.ok ? " ✓" : ""}
                      </>
                    )
                    : "Never pinged — click the button above after your next catalog update."}
                </div>

                <div className="rounded-lg border border-stone-200 bg-stone-50/50 p-3 text-xs text-stone-600">
                  <p className="font-semibold text-stone-800">How to submit on GSC:</p>
                  <ol className="mt-1.5 list-decimal space-y-0.5 pl-5">
                    <li>Click the "Submit to Google Search Console" button above.</li>
                    <li>In the GSC sitemaps screen, paste{" "}
                      <code className="rounded bg-white px-1 font-mono text-[11px]">
                        /api/seo/sitemap-full.xml
                      </code>{" "}
                      and click Submit.
                    </li>
                    <li>GSC re-crawls within 24 hours; IndexNow updates Bing/Yandex within minutes.</li>
                  </ol>
                </div>
              </div>
            </section>

            {/* Section 3 — Nightly IndexNow Cron */}
            <section
              data-testid="seo-card-cron"
              className="mt-6 rounded-2xl border border-stone-200 bg-white p-6 shadow-sm"
            >
              <header className="mb-4 flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-stone-100 p-2 text-stone-700">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-stone-900">
                      Nightly IndexNow auto-ping
                    </h2>
                    <p className="text-xs text-stone-500">
                      Self-driving SEO loop — fires daily at 03:00 UTC
                      and pings Bing/Yandex with every <strong>pair</strong>,{" "}
                      <strong>product</strong>, and <strong>medication</strong>{" "}
                      URL updated since the last run.
                    </p>
                  </div>
                </div>
                <StatusPill
                  ok={!!status.cron?.enabled}
                  okLabel="Enabled"
                  warnLabel="Disabled"
                  testid="cron-status-pill"
                />
              </header>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-600"
                     data-testid="cron-schedule-info">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                    Schedule
                  </p>
                  <p className="mt-1 text-stone-900">
                    {status.cron?.schedule || "Daily at 03:00 UTC"}
                  </p>
                  <p className="mt-0.5 text-[11px] text-stone-500">
                    Next run:{" "}
                    {status.cron?.next_run
                      ? new Date(status.cron.next_run).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                          timeZone: "UTC",
                        }) + " UTC"
                      : "—"}
                  </p>
                </div>
                <div className="rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-600"
                     data-testid="cron-last-run-info">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                    Last run
                  </p>
                  <p className="mt-1 text-stone-900">
                    {status.cron?.last_ping_at
                      ? relativeTime(status.cron.last_ping_at)
                      : "Never"}
                  </p>
                  <p className="mt-0.5 text-[11px] text-stone-500">
                    {status.cron?.last_run_status === "ok"
                      ? `Pinged ${status.cron.last_url_count} URL${status.cron.last_url_count === 1 ? "" : "s"} ✓`
                      : status.cron?.last_run_status === "noop"
                        ? "No new URLs — noop"
                        : status.cron?.last_run_status === "partial"
                          ? `${status.cron.last_url_count} URL${status.cron.last_url_count === 1 ? "" : "s"} pinged (partial)`
                          : status.cron?.last_run_status === "seeded"
                            ? "Seeded high-water mark"
                            : "—"}
                  </p>
                  {status.cron?.last_breakdown && (
                    <p className="mt-1 text-[11px] text-stone-500"
                       data-testid="cron-breakdown">
                      Pairs {status.cron.last_breakdown.pairs} ·
                      Products {status.cron.last_breakdown.products} ·
                      Meds {status.cron.last_breakdown.medications}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="button"
                  onClick={onCronRunNow}
                  disabled={cronBusy || !status.cron?.enabled}
                  data-testid="cron-run-now-button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-stone-900 hover:border-stone-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {cronBusy
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Zap className="h-3.5 w-3.5" />}
                  Force-run cron now
                </button>
                {!status.cron?.enabled && (
                  <p className="mt-2 text-xs text-amber-700"
                     data-testid="cron-disabled-warning">
                    Set <code className="rounded bg-amber-100 px-1 font-mono text-[11px]">INDEXNOW_KEY</code>{" "}
                    in <code className="rounded bg-amber-100 px-1 font-mono text-[11px]">backend/.env</code>{" "}
                    to enable nightly auto-pings.
                  </p>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
